/**
 * RouteSurface — the scroll container a routed page renders inside.
 *
 * There are two panes that render routed pages: the primary one, via the
 * layout routes in `AppRouter`, and the side pane, via `APP_ROUTES`. Both had
 * their own copy of the wrapper, and the copies drifted: the primary's
 * full-bleed wrapper was changed from `overflow-hidden` to `overflow-y-auto`
 * on 2026-08-03 so no page could be silently clipped, and the side pane's copy
 * was left behind — so the bug that fix made structurally impossible stayed
 * alive in the side pane for six weeks. One declaration, so that cannot recur.
 *
 * It is also the element whose scroll position a live tab has to keep. Scroll
 * position survives `<Activity>` only for DOM inside the retained subtree, so
 * this component must be rendered per tab, never hoisted onto the pane as
 * shared chrome. See docs/plans/2026-09-11-live-tabs-design.md, phase 4.
 */
import React from 'react';

/** Which of the two page shapes a route renders as. Mirrors `AppRoute.layout`. */
export type RouteLayout = 'full-bleed' | 'constrained';

/**
 * `overflow-x-hidden` is load-bearing, not decorative: `overflow-y-auto` alone
 * leaves `overflow-x: visible`, which CSS then computes to `auto`, sprouting a
 * horizontal scrollbar on every page with any horizontal overflow.
 *
 * `min-h-0` on the full-bleed surface keeps the scroller's height bounded by
 * its flex parent instead of by its content.
 */
const SURFACE_CLASSES: Record<RouteLayout, string> = {
  'full-bleed': 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto',
  constrained: 'flex-1 overflow-x-hidden overflow-y-auto scroll-auto px-3 sm:px-6',
};

interface Props {
  /** The route's layout shape. */
  layout: RouteLayout;
  /**
   * The element to render. The primary pane is the document's `main` landmark;
   * the side pane is not, so it stays a plain `div` rather than declaring a
   * second one.
   */
  as?: 'main' | 'div';
  children: React.ReactNode;
}

/**
 * Render a routed page inside the scroll container its layout calls for.
 *
 * A page that owns its own scrolling is unaffected — it sizes to `h-full`, so
 * this scroller has nothing to scroll. A page that genuinely needs clipping
 * opts out with `overflow-hidden` on its own root.
 */
export const RouteSurface: React.FC<Props> = ({ layout, as: Element = 'div', children }) => (
  <Element className={SURFACE_CLASSES[layout]} data-testid="route-surface">
    {layout === 'constrained' ? (
      <div className="mt-3 min-h-full max-w-4xl" data-testid="route-surface-width-cap">
        {children}
      </div>
    ) : (
      children
    )}
  </Element>
);
